import { Link, useNavigate } from "react-router-dom"
import {
  AuthPhoneBackdrop,
  AuthWarmBackdrop,
  authButton,
} from "../Components/AuthLayout"
import dealMasterLogo from "@/images/backgrounds/logo-without-dice.png"

// Where a request goes when somebody can no longer sign in and so cannot use
// the in-app button. Same mailbox as the privacy policy, on purpose: one place
// to change when support moves.
const CONTACT_EMAIL = "privacy@dealmaster.game"

// Google Play asks the page to name the app and the developer as they appear on
// the store listing, so both are stated once here rather than inline.
const APP_NAME = "Deal Master"
const DEVELOPER_NAME = "Deal Master"

const LAST_UPDATED = "1 October 2026"

const bodyText =
  "font-third text-xl leading-relaxed text-brown-800 mobile:text-3xl mobile:text-primary/80"

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mb-10 sm:mb-6">
    <h2 className="mb-3 font-third text-2xl font-semibold text-brown-700 mobile:text-4xl mobile:text-amber-300 sm:mb-2">
      {title}
    </h2>
    <div className={`flex flex-col gap-3 ${bodyText}`}>{children}</div>
  </section>
)

const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="ml-6 flex list-disc flex-col gap-2 marker:text-red-500 mobile:marker:text-amber-300">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
)

const Steps: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ol className="ml-6 flex list-decimal flex-col gap-2 marker:text-red-500 mobile:marker:text-amber-300">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ol>
)

const inlineLink =
  "font-semibold text-red-500 underline underline-offset-4 mobile:text-amber-300"

interface DeleteAccountProps {}

const DeleteAccount: React.FC<DeleteAccountProps> = () => {
  const nav = useNavigate()

  // This URL goes to the app stores directly, so a visitor usually arrives with
  // no history behind them and there is nothing to go back to.
  const goBack = () => {
    if (window.history.length > 1) {
      nav(-1)
    } else {
      nav("/login")
    }
  }

  return (
    <div className="relative h-full w-full overflow-y-auto bg-primary px-8 py-12 sm:py-6 mobile:bg-transparent mobile:pb-[max(16px,var(--safe-bottom))] mobile:pl-[max(20px,var(--safe-left))] mobile:pr-[max(20px,var(--safe-right))] mobile:pt-[max(16px,var(--safe-top))]">
      {/* `fixed`, not `absolute`: this column is the scroller, so an absolutely
          positioned backdrop would only cover the first screenful. */}
      <div className="pointer-events-none fixed inset-0">
        <AuthPhoneBackdrop />
        <AuthWarmBackdrop />
      </div>

      <div className="relative mx-auto w-full max-w-[900px] mobile:rounded-[28px] mobile:border mobile:border-white/20 mobile:bg-black/60 mobile:px-6 mobile:py-6 mobile:shadow-[0_24px_70px_rgba(0,0,0,0.65)] mobile:backdrop-blur-xl">
        <img
          src={dealMasterLogo}
          className="mx-auto mb-6 hidden w-full max-w-[220px] drop-shadow-[0_8px_28px_rgba(0,0,0,0.8)] mobile:block sm:!mb-3 sm:!max-w-[140px]"
          alt={APP_NAME}
        />

        <div className="form-heading mb-2 text-center uppercase mobile:!text-primary sm:mb-1 sm:!text-5xl">
          Delete your account
        </div>
        <div className="mb-10 text-center font-third text-base text-gray-500 mobile:text-3xl mobile:text-primary/70 sm:mb-6">
          {APP_NAME} by {DEVELOPER_NAME} &middot; Last updated {LAST_UPDATED}
        </div>

        <Section title="About this page">
          <p>
            This page explains how to request that your {APP_NAME} account, and
            the data held with it, is deleted. It applies to the {APP_NAME} game
            published by {DEVELOPER_NAME} on Google Play and on the web.
          </p>
          <p>
            {APP_NAME} accounts are created by signing in with Google, so
            deleting your account here removes your {APP_NAME} account only. Your
            Google account itself is untouched.
          </p>
        </Section>

        <Section title="Delete your account from inside the app">
          <p>
            This is the fastest route and involves no waiting: the deletion
            happens the moment you confirm.
          </p>
          <Steps
            items={[
              <>
                Open {APP_NAME} and sign in with the account you want deleted.
              </>,
              <>
                Open the menu and choose <strong>Settings</strong> (the gear
                icon).
              </>,
              <>
                Scroll to the <strong>Danger zone</strong> panel at the bottom of
                that page.
              </>,
              <>
                Tap <strong>Delete account</strong>.
              </>,
              <>
                In the confirmation box, type <strong>DELETE</strong> and tap{" "}
                <strong>Delete forever</strong>.
              </>,
            ]}
          />
          <p>
            Your account and its data are erased immediately and you are signed
            out. There is no undo and no grace period, so only confirm if you are
            sure.
          </p>
        </Section>

        <Section title="If you cannot sign in">
          <p>
            If you have lost access to the Google account you signed up with,
            email us from any address at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className={inlineLink}>
              {CONTACT_EMAIL}
            </a>{" "}
            with the subject <strong>Delete my account</strong>, and tell us the
            email address or player name you used in {APP_NAME}.
          </p>
          <p>
            We may ask a question or two to confirm the account is yours. Once
            confirmed, we delete it within 30 days and write back to say it is
            done.
          </p>
        </Section>

        <Section title="What is deleted">
          <p>Deleting your account permanently erases:</p>
          <Bullets
            items={[
              "Your account record: player name, email address, and the Google sign-in link",
              "Your profile picture, including any earlier pictures you uploaded",
              "Your lifetime statistics and your rating",
              "Your friends list, and your place on other players' friends lists and pending invites",
              "Your position in any matchmaking queue, and your signed-in session",
            ]}
          />
        </Section>

        <Section title="What is kept, and for how long">
          <Bullets
            items={[
              <>
                <strong>Finished game histories.</strong> A saved game is a
                single record shared by everyone who played it, so it stays in
                the other players' history and your name may still appear there
                as a past opponent. These records hold the results of the game
                only: no email address, no picture, no contact details.
              </>,
              <>
                <strong>Server and error logs.</strong> Routine technical logs
                may mention the account for a short period before they rotate
                out. They are kept only for security and debugging.
              </>,
              <>
                <strong>Anything the law requires us to keep.</strong> For
                example a record that you asked for deletion. We keep only that,
                and nothing more.
              </>,
            ]}
          />
          <p>
            Nothing that is kept is used to rebuild your account or to contact
            you, and you are free to create a new account at any time.
          </p>
        </Section>

        <Section title="Purchases">
          <p>
            {APP_NAME} does not sell subscriptions, and deleting your account
            does not change anything on your Google account. Refund requests for
            a Google Play purchase go through Google Play.
          </p>
        </Section>

        <Section title="Questions">
          <p>
            For anything else about your account or the data we hold, write to{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className={inlineLink}>
              {CONTACT_EMAIL}
            </a>
            . Our{" "}
            <Link to="/privacy-policy" className={inlineLink}>
              Privacy Policy
            </Link>{" "}
            describes what we collect and why.
          </p>
        </Section>

        <div className="mt-12 flex flex-col items-center gap-4 border-t border-brown-300 pt-8 mobile:border-white/20 sm:mt-6 sm:pt-4">
          <button
            onClick={goBack}
            data-testid="delete-account-back-btn"
            className={`${authButton} max-w-[320px] border-2 border-brown-700 bg-transparent text-brown-700 hover:-translate-y-0.5 hover:bg-brown-700 hover:text-primary mobile:max-w-none mobile:border-amber-400/60 mobile:text-amber-200 mobile:hover:bg-amber-400 mobile:hover:text-black`}
          >
            Go back
          </button>
          <Link
            to="/login"
            className="font-third text-base text-gray-500 underline-offset-4 transition-colors hover:text-brown-700 hover:underline mobile:text-3xl mobile:text-primary/70 mobile:hover:text-amber-300"
          >
            Back to login
          </Link>
        </div>
      </div>
    </div>
  )
}

export default DeleteAccount
